import { useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import styles from './Toast.module.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

const ICON_MAP: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const LABEL_MAP: Record<ToastType, string> = {
  success: 'Sucesso',
  error: 'Erro',
  warning: 'Aviso',
  info: 'Informação',
};

const AUTO_DISMISS_MS = 5000;

/**
 * Individual toast notification component.
 *
 * - Auto-dismisses after 5 seconds.
 * - Slide-in animation from top-right.
 * - Accessible with role="status" and aria-live="polite".
 * - Dismiss button with keyboard support.
 */
function ToastItem({ toast, onDismiss }: ToastProps) {
  const Icon = ICON_MAP[toast.type];

  const handleDismiss = useCallback(() => {
    onDismiss(toast.id);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    const timer = window.setTimeout(handleDismiss, AUTO_DISMISS_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [handleDismiss]);

  const classNames = [styles.toast, styles[toast.type]].join(' ');

  return (
    <div
      className={classNames}
      role="status"
      aria-live="polite"
      aria-label={`${LABEL_MAP[toast.type]}: ${toast.message}`}
    >
      <Icon size={20} className={styles.icon} aria-hidden="true" />
      <p className={styles.message}>{toast.message}</p>
      <button
        type="button"
        className={styles.dismissButton}
        onClick={handleDismiss}
        aria-label="Fechar notificação"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

/**
 * Container for stacked toast notifications.
 * Renders in top-right corner of viewport.
 */
export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} aria-label="Notificações">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
